import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertCircle, ChevronDown, Eye, EyeOff, Layers } from 'lucide-react';

import { useCategories } from '../../../contexts/CategoriesContext';
import type { QuickInferenceFrame } from '../../../helpers/api/quick-inference/quick-inference';
import { collectCategoryScores } from './QuickInferenceMeasurementsPanel';
import {
  collectQualityFlagRows,
  displayedFrameViews,
  formatViewLabel,
  qualityFlagImpliesStructurePresent,
  type QualityFlagRow,
} from './quickInferenceUtils';
import { useCategoryLabels } from './useCategoryLabels';

function normalizeQualFlagCode(raw: string): string {
  return String(raw ?? '')
    .replace(/^QUAL_FLAG__/, '')
    .replace(/_(segment_polygon|box_segment|segment_box|segment|polygon|box)$/i, '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '_');
}

interface Props {
  frame: QuickInferenceFrame;
  activeViewCode?: string | null;
  hiddenCategoryIds?: number[];
  onToggleCategoryVisibility?: (categoryId: number) => void;
}

export function QuickInferenceQualityPanel({
  frame,
  activeViewCode,
  hiddenCategoryIds = [],
  onToggleCategoryVisibility,
}: Props) {
  const { t } = useTranslation(['GeneralViewer']);
  const { codeById, getColorForCategoryId } = useCategories();
  const { labelForCategoryId } = useCategoryLabels();

  const viewCodes = useMemo(() => displayedFrameViews(frame), [frame]);

  const rowsByView = useMemo(() => {
    const rows = collectQualityFlagRows(frame, viewCodes);
    const grouped = new Map<string, QualityFlagRow[]>();
    for (const row of rows) {
      const list = grouped.get(row.viewCode) ?? [];
      list.push(row);
      grouped.set(row.viewCode, list);
    }
    return grouped;
  }, [frame, viewCodes]);

  const visibleViewCodes = useMemo(
    () => viewCodes.filter((code) => rowsByView.has(code)),
    [viewCodes, rowsByView]
  );

  const [collapsedViews, setCollapsedViews] = useState<Set<string>>(() => new Set());

  useEffect(() => {
    if (activeViewCode && rowsByView.has(activeViewCode)) {
      setCollapsedViews((prev) => {
        if (!prev.has(activeViewCode)) return prev;
        const next = new Set(prev);
        next.delete(activeViewCode);
        return next;
      });
    }
  }, [activeViewCode, rowsByView]);

  const toggleViewCollapsed = (viewCode: string) => {
    setCollapsedViews((prev) => {
      const next = new Set(prev);
      if (next.has(viewCode)) next.delete(viewCode);
      else next.add(viewCode);
      return next;
    });
  };

  const segsByCategory = useMemo(() => {
    // Biometry stores structure + MEAS__* twin rows — count structures only;
    // count a measurement only when its structure sibling is missing.
    const map = new Map<number, { user: number; ai: number }>();
    const structureKeys = new Set<string>();

    const isMeasSeg = (seg: (typeof frame.segmentations)[number]) => {
      const label = String(seg.label ?? '');
      return (
        seg.segment_type === 'MEASUREMENT' ||
        seg.segment_type === 'measurement' ||
        label.startsWith('MEAS__')
      );
    };

    const bump = (id: number, source: 'user' | 'ai') => {
      const entry = map.get(id) ?? { user: 0, ai: 0 };
      if (source === 'user') entry.user += 1;
      else entry.ai += 1;
      map.set(id, entry);
    };

    for (const seg of frame.segmentations || []) {
      const id = Number(seg.category_id);
      if (!Number.isFinite(id) || isMeasSeg(seg)) continue;
      const source = seg.is_manual === true ? 'user' : 'ai';
      structureKeys.add(`${id}::${source}`);
      bump(id, source);
    }
    for (const seg of frame.segmentations || []) {
      const id = Number(seg.category_id);
      if (!Number.isFinite(id) || !isMeasSeg(seg)) continue;
      const source = seg.is_manual === true ? 'user' : 'ai';
      if (structureKeys.has(`${id}::${source}`)) continue;
      bump(id, source);
    }
    return map;
  }, [frame.segmentations]);

  const scoresByCategory = useMemo(
    () => collectCategoryScores(frame.segmentations),
    [frame.segmentations]
  );

  const getFlagDisplayName = (flag: QualityFlagRow): string => {
    const codeRaw = codeById[flag.categoryId] ?? labelForCategoryId(flag.categoryId);
    const codeNorm = normalizeQualFlagCode(codeRaw);
    const base = codeNorm
      ? t(`qualityFlag.${codeNorm}`, {
          defaultValue: labelForCategoryId(flag.categoryId),
        })
      : labelForCategoryId(flag.categoryId);

    if (!flag.expectedVisible) {
      return t('qualityFlag.notVisibleSuffix', {
        defaultValue: `${base} Not Visible`,
        label: base,
      });
    }
    return base;
  };

  if (visibleViewCodes.length === 0) {
    return (
      <div className="rounded-xl border border-dashed border-gray-200 bg-gray-50/80 px-3 py-6 text-center dark:border-gray-700 dark:bg-gray-900/40">
        <div className="mx-auto mb-2 flex h-9 w-9 items-center justify-center rounded-full bg-white text-gray-400 shadow-sm dark:bg-gray-800">
          <Layers size={16} />
        </div>
        <p className="text-xs font-semibold text-gray-700 dark:text-gray-200">No view assigned</p>
        <p className="mt-1 text-[11px] leading-relaxed text-gray-500 dark:text-gray-400">
          Assign a view on this frame to load quality criteria here.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {visibleViewCodes.map((viewCode) => {
        const flags = rowsByView.get(viewCode) ?? [];
        const isActive = activeViewCode != null && activeViewCode === viewCode;
        const isCollapsed = collapsedViews.has(viewCode);
        const passedCount = flags.filter((f) => f.passed).length;

        return (
          <div
            key={viewCode}
            className={`overflow-hidden rounded-xl border ${
              isActive
                ? 'border-violet-200 dark:border-violet-800/60'
                : 'border-gray-200 dark:border-gray-700'
            }`}
          >
            <button
              type="button"
              onClick={() => toggleViewCollapsed(viewCode)}
              className={`flex w-full items-center gap-2 px-3 py-2.5 text-left transition ${
                isActive
                  ? 'bg-violet-50/80 dark:bg-violet-900/20'
                  : 'bg-gray-50/80 hover:bg-gray-100/80 dark:bg-gray-900/40 dark:hover:bg-gray-900/70'
              }`}
              aria-expanded={!isCollapsed}
            >
              <ChevronDown
                size={14}
                className={`shrink-0 text-gray-500 transition-transform dark:text-gray-400 ${
                  isCollapsed ? '-rotate-90' : ''
                }`}
              />
              <span
                className={`min-w-0 flex-1 truncate text-xs font-semibold ${
                  isActive
                    ? 'text-violet-700 dark:text-violet-300'
                    : 'text-gray-700 dark:text-gray-300'
                }`}
              >
                {formatViewLabel(viewCode)}
              </span>
              <span className="shrink-0 text-[10px] font-medium tabular-nums text-gray-400 dark:text-gray-500">
                {passedCount}/{flags.length}
              </span>
            </button>

            {!isCollapsed ? (
              <div className="space-y-1.5 border-t border-gray-100 p-2 dark:border-gray-700/80">
                {flags.map((flag) => {
                  const label = getFlagDisplayName(flag);
                  const structurePresent = qualityFlagImpliesStructurePresent(flag);
                  const segCounts = segsByCategory.get(flag.categoryId);
                  const score = scoresByCategory.get(flag.categoryId);
                  const isHidden = hiddenCategoryIds.includes(flag.categoryId);
                  const color =
                    structurePresent && !isHidden && getColorForCategoryId
                      ? getColorForCategoryId(flag.categoryId)
                      : '#64748b';

                  return (
                    <div
                      key={`${viewCode}-${flag.categoryId}-${flag.ruleType}`}
                      className={`flex items-center justify-between gap-1.5 rounded-lg border px-2 py-2 transition ${
                        flag.passed
                          ? 'border-green-100 bg-green-50/50 dark:border-green-900/30 dark:bg-green-900/10'
                          : 'border-orange-100 bg-white dark:border-orange-900/30 dark:bg-gray-900/30'
                      } ${isHidden ? 'opacity-70' : ''}`}
                    >
                      <div className="flex min-w-0 flex-1 items-center gap-1.5">
                        <button
                          type="button"
                          disabled={!onToggleCategoryVisibility || !structurePresent}
                          onClick={() => onToggleCategoryVisibility?.(flag.categoryId)}
                          className={`shrink-0 rounded-md p-0.5 transition ${
                            !structurePresent
                              ? 'cursor-default text-slate-300 opacity-60 dark:text-slate-600'
                              : isHidden
                                ? 'text-slate-400 hover:bg-slate-100 dark:hover:bg-gray-800'
                                : 'text-blue-500 hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-900/30'
                          }`}
                          title={
                            !structurePresent
                              ? flag.ruleType === 'not_visible'
                                ? 'Expected not visible — structure absent'
                                : 'Expected visible — structure missing'
                              : isHidden
                                ? 'Show drawing on canvas'
                                : 'Hide drawing on canvas'
                          }
                        >
                          {isHidden || !structurePresent ? <EyeOff size={16} /> : <Eye size={16} />}
                        </button>

                        <span
                          className="h-2.5 w-2.5 shrink-0 rounded-full"
                          style={{
                            backgroundColor: color,
                            boxShadow: structurePresent ? '0 0 8px rgba(0,0,0,0.1)' : 'none',
                          }}
                        />

                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-center gap-1.5">
                            <span className="truncate text-xs font-medium text-gray-700 dark:text-gray-200">
                              {label}
                            </span>
                            {score != null ? (
                              <span className="rounded-full bg-violet-100 px-1.5 py-0.5 text-[10px] font-semibold text-violet-700 dark:bg-violet-900/40 dark:text-violet-300">
                                {Math.round(score * 100)}%
                              </span>
                            ) : null}
                            {segCounts?.ai ? (
                              <span className="rounded border border-purple-200 bg-purple-100 px-1.5 py-0.5 text-[10px] font-bold text-purple-700 dark:border-purple-500/30 dark:bg-purple-900/40 dark:text-purple-300">
                                AI{segCounts.ai > 1 ? ` ×${segCounts.ai}` : ''}
                              </span>
                            ) : null}
                            {segCounts?.user ? (
                              <span className="rounded border border-blue-200 bg-blue-100 px-1.5 py-0.5 text-[10px] font-bold text-blue-700 dark:border-blue-500/30 dark:bg-blue-900/40 dark:text-blue-300">
                                You{segCounts.user > 1 ? ` ×${segCounts.user}` : ''}
                              </span>
                            ) : null}
                          </div>
                          <p className="mt-0.5 text-[10px] text-slate-400">
                            {flag.ruleType === 'not_visible' ? 'Must not be visible' : 'Must be visible'}
                          </p>
                        </div>
                      </div>

                      <span
                        className={`inline-flex shrink-0 items-center gap-1 text-[10px] font-semibold uppercase ${
                          flag.passed ? 'text-green-600' : 'text-orange-500'
                        }`}
                      >
                        {flag.passed ? (
                          <span className="h-2 w-2 rounded-full bg-green-500" />
                        ) : (
                          <AlertCircle size={12} />
                        )}
                        {flag.passed ? 'Pass' : 'Check'}
                      </span>
                    </div>
                  );
                })}
              </div>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}
